import React, { useRef } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { reorderWeekdayDependentLanguage } from './helper';
import WeekDay from './WeekDay';
import MonthCaption from './MonthCaption';
import { MonthHandler } from '../types';
import DayItem from './DayItem';
import DayItemMobile from './DayItemMobile';
import { FnOnDayClick, FnOnDayHover } from './hooks';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import Box from '../../../elements/Box';

export type MonthItemProps = {
  captionItem: unknown;
  month: MonthHandler;
  weekdayItem: unknown;
  dayItems: any;
  renderDay?: Function;
  onDayClick: FnOnDayClick;
  onDayHover?: FnOnDayHover;
  showYear?: boolean;
  showWeekDayNames?: boolean;
  isMobile?: boolean;
  isDateRange?: boolean;
  preSelectedDate?: Date;
  weekDaysContainer?: any;
  componentName?: string;
  calendarMonthContainer?: $TSFixMe;
  setWasOpenPopup?: (value: boolean) => void;
  setUserInteraction?: (value: boolean) => void;
  dayWidthSetting?: {
    enableDynamicWidthCell?: boolean;
    width?: number;
  };
};
const MonthItem = (
  {
    captionItem,
    month,
    weekdayItem,
    dayItems,
    onDayHover = () => {},
    renderDay,
    onDayClick = () => {},
    showYear,
    showWeekDayNames = true,
    weekDaysContainer,
    calendarMonthContainer,
    setWasOpenPopup,
    isMobile = false,
    isDateRange = false,
    preSelectedDate,
    setUserInteraction,
    componentName,
    dayWidthSetting = {
      enableDynamicWidthCell: false,
      width: 39
    }
  }: MonthItemProps,
  ref: any
) => {
  const Tag = isMobile ? DayItemMobile : DayItem;
  const days = React.useMemo(() => month.getMonthData(), [month]);
  const { localizationData } = React.useContext(Context);
  const weekdays = reorderWeekdayDependentLanguage(localizationData);
  const weeksRef = useRef<any>(null);
  const [minWidth, setMinWidth] = React.useState(39);

  const renderWeekDayNames = showWeekDayNames && (
    <Box className="calendar-days-title calendar-month" role="row" {...weekDaysContainer}>
      {weekdays.map((offset) => {
        return (
          <WeekDay key={`month-${month.getMonth()}-${offset}`} weekday={offset} {...weekdayItem} />
        );
      })}
    </Box>
  );
  const dimensions = useWindowDimensions();
  React.useEffect(() => {
    if (
      isMobile &&
      weeksRef.current?.offsetWidth &&
      weeksRef.current.offsetWidth / 7 !== minWidth
    ) {
      setMinWidth(weeksRef.current.offsetWidth / 7);
    }
  }, [isMobile, weeksRef, dimensions, minWidth]);

  let renderWeeks;
  let dayItemsProps = { ...dayItems };

  const newCmpStyles =
    dayWidthSetting?.enableDynamicWidthCell === true
      ? { ...dayItems['cmp-styles'], 'min-width': `${minWidth}px` }
      : {
          ...dayItems['cmp-styles'],
          'min-width': `${dayWidthSetting?.width ?? 39}px`,
          width: `${dayWidthSetting?.width ?? 39}px`
        };

  if (isMobile) {
    dayItemsProps = {
      ...dayItems,
      'cmp-styles': newCmpStyles
    };
  }

  if (renderDay) {
    renderWeeks = days.map((week, i) => (
      <Box
        focusReferences={weeksRef}
        className="calendar-week"
        key={`month-${month}-${i}`}
        role="row"
      >
        {week.map((dayData, index) => {
          return renderDay({ dayData, index, onDayClick, onDayHover, dayItemsProps });
        })}
      </Box>
    ));
  } else {
    renderWeeks = days.map((week, i) => (
      <Box
        focusReferences={weeksRef}
        className="calendar-week"
        key={`month-${month}-${i}`}
        role="row"
      >
        {week.map((day, index) => (
          <Tag
            {...dayItemsProps}
            dayData={day}
            onDayClick={onDayClick}
            onDayHover={onDayHover}
            key={`${day?.date?.getDate()}-${day?.date?.getMonth()}-${index}`}
            setWasOpenPopup={setWasOpenPopup}
            setUserInteraction={setUserInteraction}
            componentName={componentName}
          />
        ))}
      </Box>
    ));
  }

  return (
    <Box
      className={`calendar-month-container calendar-month-container-${
        isDateRange ? 'daterange' : 'datepicker'
      }`}
      {...calendarMonthContainer}
      key={`month-${month.getMonth().getMonth()}`}
      focusReferences={ref}
    >
      <MonthCaption date={month.getMonth()} {...captionItem} showYear={showYear} />
      <Box role="table">
        {renderWeekDayNames}
        {renderWeeks}
      </Box>
    </Box>
  );
};
export default React.forwardRef(MonthItem);
